import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  ArrowLeft,
  Send, 
  Smartphone, 
  Zap, 
  PiggyBank,
  ArrowUpRight,
  ArrowDownLeft,
  Download,
  CreditCard,
  Gift,
  Eye,
  EyeOff,
  Copy,
  Snowflake,
  Building2,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f04fecbe-9399-4471-a47d-7dd5f6d0f107/artifacts/x9r7wgb6_App%20Development.png";

export const BankingSection = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCardDetails, setShowCardDetails] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const res = await api.get('/transactions');
      setTransactions(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCardNumber = () => {
    navigator.clipboard.writeText(user?.card_number?.replace(/\s/g, ''));
    toast.success('Card number copied');
  };

  const toggleCardFreeze = async () => {
    try {
      const endpoint = user?.card_frozen ? '/user/card/unfreeze' : '/user/card/freeze';
      await api.put(endpoint);
      await refreshUser();
      toast.success(user?.card_frozen ? 'Card unfrozen' : 'Card frozen');
    } catch (error) {
      toast.error('Failed to update card status');
    }
  };

  const features = [
    { icon: Send, label: 'Transfer', path: 'transfer', color: 'text-blue-500' },
    { icon: Smartphone, label: 'Airtime', path: 'airtime', color: 'text-green-500' },
    { icon: Zap, label: 'Electricity', path: 'electricity', color: 'text-yellow-500' },
    { icon: PiggyBank, label: 'Savings', path: 'savings', color: 'text-purple-500' },
  ];

  const formatAmount = (amount, type) => {
    const isPositive = ['deposit', 'bonus'].includes(type);
    return `${isPositive ? '+' : '-'}R${Math.abs(amount).toFixed(2)}`;
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownLeft className="w-4 h-4 text-green-500" />;
      case 'bonus': return <Gift className="w-4 h-4 text-afriperx-gold" />;
      case 'savings': return <PiggyBank className="w-4 h-4 text-purple-500" />;
      case 'airtime': return <Smartphone className="w-4 h-4 text-green-500" />;
      case 'electricity': return <Zap className="w-4 h-4 text-yellow-500" />;
      case 'payment': return <Building2 className="w-4 h-4 text-blue-500" />;
      default: return <ArrowUpRight className="w-4 h-4 text-red-500" />;
    }
  };

  // Main banking view
  const BankingMain = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-syne text-2xl font-bold text-gray-900">Banking</h1>
        <PayDialog onSuccess={() => { fetchTransactions(); refreshUser(); }} />
      </div>

      {/* Diamond Banking Card */}
      <div className="banking-card-container">
        <div className="relative w-full max-w-md mx-auto md:max-w-lg lg:max-w-xl bg-white rounded-2xl p-4 shadow-sm border border-gray-200" data-testid="banking-card">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '1.6/1' }}>
            <img
              src="https://customer-assets.emergentagent.com/job_ffde13bd-7ce1-4213-a085-9ac4901b9cce/artifacts/vuwn7j5r_Diamond%20Card%20Png.png"
              alt="AfriPerx Diamond Card"
              className="w-full h-full object-contain"
            />
            {/* Client name overlay on bottom left */}
            <div className="absolute bottom-4 left-6 md:bottom-6 md:left-8">
              <p className="text-white text-lg md:text-xl lg:text-2xl font-bold tracking-wide drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                {user?.name} {user?.surname}
              </p>
            </div>

            {/* Frozen overlay */}
            {user?.card_frozen && (
              <div className="absolute inset-0 bg-blue-900/60 backdrop-blur-sm flex items-center justify-center rounded-2xl">
                <div className="text-center">
                  <Snowflake className="w-12 h-12 text-blue-400 mx-auto mb-3 animate-pulse" />
                  <p className="text-blue-300 font-semibold text-lg">Card Frozen</p>
                </div>
              </div>
            )}
          </div>

          {/* Card action buttons */}
          <div className="flex justify-center gap-3 mt-3">
            <button 
              onClick={toggleCardFreeze}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                user?.card_frozen 
                  ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
              data-testid="freeze-card-btn"
            >
              <Snowflake className="w-4 h-4" />
              {user?.card_frozen ? 'Unfreeze' : 'Freeze Card'}
            </button>
            <button 
              onClick={() => setShowCardDetails(!showCardDetails)}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 text-sm font-medium flex items-center gap-2 transition-colors"
              data-testid="toggle-card-details"
            >
              {showCardDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showCardDetails ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          {/* Card details (expandable) */}
          {showCardDetails && (
            <Card className="mt-3 bg-white border border-gray-200 p-4 rounded-xl shadow-sm">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-500 text-xs uppercase">Card Number</p>
                  <p className="text-gray-900 font-mono text-sm mt-1">{user?.card_number}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase">Valid Thru</p>
                  <p className="text-gray-900 font-mono text-sm mt-1">{user?.card_expiry}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase">CVV</p>
                  <p className="text-gray-900 font-mono text-sm mt-1">{user?.card_cvv}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="card-raoul p-5">
          <p className="text-gray-500 text-sm mb-1">Available Balance</p>
          <p className="text-gray-900 text-3xl font-bold">R{user?.main_balance?.toFixed(2)}</p>
        </Card>
        <Card className="card-raoul p-5">
          <p className="text-gray-500 text-sm mb-1">Savings</p>
          <p className="text-gray-900 text-3xl font-bold">R{user?.savings_balance?.toFixed(2)}</p>
        </Card>
      </div>

      {/* Investment Button */}
      <Button
        onClick={() => toast.info('Investment portfolio coming soon! Stay tuned.')}
        className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white h-14 text-base rounded-xl shadow-lg"
        data-testid="investment-btn"
      >
        <TrendingUp className="w-5 h-5 mr-3" />
        Investment
      </Button>

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-3">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <button
              key={feature.path}
              onClick={() => navigate(`/dashboard/banking/${feature.path}`)}
              className="feature-btn"
              data-testid={`banking-${feature.path}`}
            >
              <Icon className={`w-6 h-6 ${feature.color}`} />
              <span className="text-gray-600 text-xs">{feature.label}</span>
            </button>
          );
        })}
      </div>

      {/* Transactions */}
      <Card className="card-raoul p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-syne text-lg font-bold text-gray-900">Transaction History</h3>
          <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-900">
            <Download className="w-4 h-4 mr-2" />
            Statement
          </Button>
        </div>

        {transactions.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No transactions yet</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {transactions.map((tx) => (
              <div 
                key={tx.id}
                className="transaction-item flex items-center justify-between p-3 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    {getTransactionIcon(tx.type)}
                  </div>
                  <div>
                    <p className="text-gray-900 text-sm font-medium">{tx.description}</p>
                    <p className="text-gray-400 text-xs">
                      {new Date(tx.created_at).toLocaleDateString()} • {tx.category}
                      {tx.recipient && ` • ${tx.recipient}`}
                    </p>
                  </div>
                </div>
                <p className={`font-medium ${
                  ['deposit', 'bonus'].includes(tx.type) ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatAmount(tx.amount, tx.type)}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );

  return (
    <Routes>
      <Route index element={<BankingMain />} />
      <Route path="transfer" element={<TransferForm onSuccess={() => { fetchTransactions(); refreshUser(); }} />} />
      <Route path="airtime" element={<AirtimeForm onSuccess={() => { fetchTransactions(); refreshUser(); }} />} />
      <Route path="electricity" element={<ElectricityForm onSuccess={() => { fetchTransactions(); refreshUser(); }} />} />
      <Route path="savings" element={<SavingsForm onSuccess={() => { fetchTransactions(); refreshUser(); }} />} />
    </Routes>
  );
};

// PAY Dialog - Payment to other banks
const PayDialog = ({ onSuccess }) => {
  const { api, user } = useAuth();
  const [formData, setFormData] = useState({
    bank: '',
    accountNumber: '',
    recipientName: '',
    amount: '',
    reference: ''
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const banks = [
    { value: 'fnb', label: 'FNB (First National Bank)' },
    { value: 'standard', label: 'Standard Bank' },
    { value: 'nedbank', label: 'Nedbank' },
    { value: 'capitec', label: 'Capitec Bank' },
    { value: 'absa', label: 'ABSA Bank' },
  ];

  const handlePay = async () => {
    if (!formData.bank || !formData.accountNumber || !formData.recipientName || !formData.amount || !formData.reference) {
      toast.error('Please fill in all fields');
      return;
    }
    if (parseFloat(formData.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (parseFloat(formData.amount) > user.main_balance) {
      toast.error('Insufficient balance');
      return;
    }
    
    setLoading(true);
    try {
      const bankName = banks.find(b => b.value === formData.bank)?.label || formData.bank;
      await api.post('/transactions', {
        type: 'payment',
        amount: parseFloat(formData.amount),
        description: `Payment to ${formData.recipientName} - ${bankName}`,
        recipient: `${formData.recipientName} | ${formData.accountNumber} | Ref: ${formData.reference}`,
        category: 'payment'
      });
      toast.success(`Payment of R${formData.amount} sent successfully`);
      setOpen(false);
      setFormData({ bank: '', accountNumber: '', recipientName: '', amount: '', reference: '' });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary" data-testid="pay-btn">
          <Send className="w-4 h-4 mr-2" />
          PAY
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-gray-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-gray-900 font-syne">Pay to Bank Account</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div>
            <Label className="text-gray-600">Select Bank</Label>
            <Select value={formData.bank} onValueChange={(v) => setFormData(prev => ({ ...prev, bank: v }))}>
              <SelectTrigger className="mt-2 bg-white border-gray-200 text-gray-900">
                <SelectValue placeholder="Choose a bank" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {banks.map(bank => (
                  <SelectItem key={bank.value} value={bank.value}>{bank.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-gray-600">Account Number</Label>
            <Input
              value={formData.accountNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
              placeholder="Enter account number"
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="pay-account-input"
            />
          </div>
          
          <div>
            <Label className="text-gray-600">Recipient Name</Label>
            <Input
              value={formData.recipientName}
              onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
              placeholder="Enter recipient's name"
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="pay-name-input"
            />
          </div>
          
          <div>
            <Label className="text-gray-600">Amount (ZAR)</Label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="pay-amount-input"
            />
            <p className="text-xs text-gray-400 mt-1">Available: R{user?.main_balance?.toFixed(2)}</p>
          </div>
          
          <div>
            <Label className="text-gray-600">Reference</Label>
            <Input
              value={formData.reference}
              onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
              placeholder="Payment reference"
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="pay-reference-input"
            />
          </div>
          
          <Button 
            onClick={handlePay} 
            disabled={loading}
            className="w-full btn-primary"
            data-testid="confirm-pay-btn"
          >
            {loading ? 'Processing...' : 'Pay Now'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Transfer Form
const TransferForm = ({ onSuccess }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ recipient: '', amount: '', description: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(formData.amount) > user.main_balance) {
      toast.error('Insufficient balance');
      return;
    }
    setLoading(true);
    try {
      await api.post('/transactions', {
        type: 'transfer',
        amount: parseFloat(formData.amount),
        description: formData.description || `Transfer to ${formData.recipient}`,
        recipient: formData.recipient,
        category: 'transfer'
      });
      toast.success('Transfer successful');
      onSuccess();
      navigate('/dashboard/banking');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/dashboard/banking')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>
      <h1 className="font-syne text-2xl font-bold text-gray-900">Transfer Money</h1>
      <Card className="card-raoul p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-gray-600">Recipient (Phone/Account)</Label>
            <Input
              value={formData.recipient}
              onChange={(e) => setFormData(prev => ({ ...prev, recipient: e.target.value }))}
              placeholder="Enter recipient"
              required
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="transfer-recipient-input"
            />
          </div>
          <div>
            <Label className="text-gray-600">Amount (ZAR)</Label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
              required
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="transfer-amount-input"
            />
            <p className="text-xs text-gray-400 mt-1">Available: R{user?.main_balance?.toFixed(2)}</p>
          </div>
          <div>
            <Label className="text-gray-600">Description (Optional)</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What's this for?"
              className="mt-2 bg-white border-gray-200 text-gray-900"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="transfer-submit-btn">
            {loading ? 'Processing...' : 'Transfer'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Airtime Form
const AirtimeForm = ({ onSuccess }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ phone: '', amount: '10', network: 'vodacom' });
  const [loading, setLoading] = useState(false);

  const amounts = ['10', '20', '50', '100', '200'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(formData.amount) > user.main_balance) {
      toast.error('Insufficient balance');
      return;
    }
    setLoading(true);
    try {
      await api.post('/transactions', {
        type: 'airtime',
        amount: parseFloat(formData.amount),
        description: `${formData.network.toUpperCase()} Airtime R${formData.amount}`,
        recipient: formData.phone,
        category: 'airtime'
      });
      toast.success('Airtime purchased successfully');
      onSuccess();
      navigate('/dashboard/banking');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/dashboard/banking')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>
      <h1 className="font-syne text-2xl font-bold text-gray-900">Buy Airtime</h1>
      <Card className="card-raoul p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-gray-600">Network</Label>
            <Select value={formData.network} onValueChange={(v) => setFormData(prev => ({ ...prev, network: v }))}>
              <SelectTrigger className="mt-2 bg-white border-gray-200 text-gray-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="vodacom">Vodacom</SelectItem>
                <SelectItem value="mtn">MTN</SelectItem>
                <SelectItem value="cellc">Cell C</SelectItem>
                <SelectItem value="telkom">Telkom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-600">Phone Number</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="0XX XXX XXXX"
              required
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="airtime-phone-input"
            />
          </div>
          <div>
            <Label className="text-gray-600">Amount</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {amounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, amount: amt }))}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    formData.amount === amt 
                      ? 'bg-afriperx-green/10 border-afriperx-gold text-afriperx-gold' 
                      : 'bg-white border-gray-200 text-gray-600'
                  }`}
                >
                  R{amt}
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="airtime-submit-btn">
            {loading ? 'Processing...' : `Buy R${formData.amount} Airtime`}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Electricity Form
const ElectricityForm = ({ onSuccess }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ meterNumber: '', amount: '100' });
  const [loading, setLoading] = useState(false);

  const amounts = ['50', '100', '200', '500', '1000'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(formData.amount) > user.main_balance) {
      toast.error('Insufficient balance');
      return;
    }
    setLoading(true);
    try {
      await api.post('/transactions', {
        type: 'electricity',
        amount: parseFloat(formData.amount),
        description: `Electricity R${formData.amount}`,
        recipient: formData.meterNumber,
        category: 'electricity'
      });
      toast.success('Electricity purchased successfully');
      onSuccess();
      navigate('/dashboard/banking');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/dashboard/banking')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>
      <h1 className="font-syne text-2xl font-bold text-gray-900">Buy Electricity</h1>
      <Card className="card-raoul p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-gray-600">Meter Number</Label>
            <Input
              value={formData.meterNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, meterNumber: e.target.value }))}
              placeholder="Enter meter number"
              required
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="electricity-meter-input"
            />
          </div>
          <div>
            <Label className="text-gray-600">Amount</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {amounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, amount: amt }))}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    formData.amount === amt 
                      ? 'bg-afriperx-green/10 border-afriperx-gold text-afriperx-gold' 
                      : 'bg-white border-gray-200 text-gray-600'
                  }`}
                >
                  R{amt}
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="electricity-submit-btn">
            {loading ? 'Processing...' : `Buy R${formData.amount} Electricity`}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Savings Form
const SavingsForm = ({ onSuccess }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [goalName, setGoalName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(amount) > user.main_balance) {
      toast.error('Insufficient balance');
      return;
    }
    setLoading(true);
    try {
      await api.post('/savings', { amount: parseFloat(amount), goal_name: goalName || 'General Savings' });
      toast.success('Savings added successfully');
      onSuccess();
      navigate('/dashboard/banking');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/dashboard/banking')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>
      <h1 className="font-syne text-2xl font-bold text-gray-900">Add to Savings</h1>
      <Card className="card-raoul p-6">
        <div className="text-center mb-6">
          <p className="text-gray-500 text-sm">Current Savings</p>
          <p className="text-gray-900 text-3xl font-bold">R{user?.savings_balance?.toFixed(2)}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-gray-600">Amount to Save (ZAR)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="mt-2 bg-white border-gray-200 text-gray-900"
              data-testid="savings-amount-input"
            />
            <p className="text-xs text-gray-400 mt-1">Available: R{user?.main_balance?.toFixed(2)}</p>
          </div>
          <div>
            <Label className="text-gray-600">Goal Name (Optional)</Label>
            <Input
              value={goalName}
              onChange={(e) => setGoalName(e.target.value)}
              placeholder="e.g., Holiday Fund"
              className="mt-2 bg-white border-gray-200 text-gray-900"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="savings-submit-btn">
            {loading ? 'Processing...' : 'Add to Savings'}
          </Button>
        </form>
      </Card>
    </div>
  );
};
